import { IRating, IRatingDetails } from '@app-types/websocket/rating.types';
import { ISubscription } from '@app-types/websocket/subscription.types';

const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const ONE_WEEK_MS = 7 * ONE_DAY_MS;
const NATIVE_REVIEW_COOLDOWN_MS = 30 * ONE_DAY_MS;
const ONE_MONTH_MS = 30 * ONE_DAY_MS;
const ONE_YEAR_MS = 365 * ONE_DAY_MS;
const MIN_SUBSCRIPTION_TIME_LEFT_MS = 7 * ONE_DAY_MS;

export function ratingTimingAllowsPrompt(ratingDetails: IRatingDetails | undefined, now: number = Date.now()): boolean {
  if (ratingDetails === undefined || ratingDetails === null) {
    return false;
  }

  if (typeof ratingDetails.accountCreated !== "number" || now - ratingDetails.accountCreated <= ONE_MONTH_MS) {
    return false;
  }

  const lastRatingTime = getLastRatingTime(ratingDetails);
  return lastRatingTime === undefined || now - lastRatingTime > ONE_YEAR_MS;
}

export function shouldDisplayRatingPrompt(state: any, now: number = Date.now()): boolean {
  if (!isEligibleRatingUser(state, now)) {
    return false;
  }

  return ratingTimingAllowsPrompt(state.rating?.ratingDetails, now);
}

export function shouldRequestNativeReview(state: any, now: number = Date.now()): boolean {
  if (!isEligibleRatingUser(state, now)) {
    return false;
  }

  const ratingDetails: IRatingDetails | undefined = state.rating?.ratingDetails;
  if (!ratingDetails || typeof ratingDetails.accountCreated !== "number"
    || now - ratingDetails.accountCreated <= ONE_MONTH_MS) {
    return false;
  }

  const latestRating = getLatestRating(ratingDetails);
  return latestRating !== undefined
    && latestRating.rating === 5
    && typeof latestRating.ratingTime === "number"
    && now - latestRating.ratingTime >= ONE_WEEK_MS
    && (typeof latestRating.requestTime !== "number"
      || now - latestRating.requestTime >= NATIVE_REVIEW_COOLDOWN_MS);
}

function getLastRatingTime(ratingDetails: IRatingDetails): number | undefined {
  const latestRating = getLatestRating(ratingDetails);
  return latestRating && latestRating.ratingTime;
}

function getLatestRating(ratingDetails: IRatingDetails): IRating | undefined {
  if (!Array.isArray(ratingDetails.ratings)) {
    return undefined;
  }

  let latestRating: IRating | undefined;
  for (const rating of ratingDetails.ratings) {
    if (typeof rating.ratingTime !== "number") {
      continue;
    }

    if (latestRating === undefined || rating.ratingTime >= (latestRating.ratingTime as number)) {
      latestRating = rating;
    }
  }

  return latestRating;
}

function isEligibleRatingUser(state: any, now: number): boolean {
  const authorization = state.user?.session?.data?.authorization;
  if (typeof authorization !== "string" || authorization.length === 0) {
    return false;
  }

  const accountId = state.user?.session?.data?.id;
  if (typeof accountId !== "number") {
    return false;
  }

  return hasActiveSubscriptionWithEnoughTimeLeft(state.subscriptions?.subscriptions, now);
}

function hasActiveSubscriptionWithEnoughTimeLeft(subscriptions: ISubscription[] | undefined, now: number): boolean {
  if (!Array.isArray(subscriptions)) {
    return false;
  }

  return subscriptions.some((subscription) =>
    subscription.disabled !== true
    && typeof subscription.expirationTime === "number"
    && subscription.expirationTime - now > MIN_SUBSCRIPTION_TIME_LEFT_MS
  );
}



// WEBPACK FOOTER //
// ./src/services/rating/rating.utils.ts